import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useUIStore, type Toast as ToastType } from '@/store/uiStore';
import { cn } from '@/lib/cn';

const variantStyles: Record<ToastType['variant'], string> = {
  success: 'border-[color:var(--color-success)]/40 [--accent:var(--color-success)]',
  error:   'border-[color:var(--color-error)]/40 [--accent:var(--color-error)]',
  info:    'border-[color:var(--color-info)]/40 [--accent:var(--color-info)]',
  warning: 'border-[color:var(--color-warning)]/40 [--accent:var(--color-warning)]',
};

const variantIcon: Record<ToastType['variant'], string> = {
  success: '✓',
  error: '!',
  info: 'i',
  warning: '!',
};

function ToastItem({ toast }: { toast: ToastType }) {
  const remove = useUIStore((s) => s.removeToast);
  const { id, duration } = toast;

  useEffect(() => {
    if (duration === null) return;
    const t = setTimeout(() => remove(id), duration);
    return () => clearTimeout(t);
  }, [id, duration, remove]);

  return (
    <motion.div
      role="status"
      layout
      initial={{ opacity: 0, x: 80, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.95, transition: { duration: 0.18 } }}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      className={cn(
        'pointer-events-auto flex w-full max-w-[360px] items-start gap-3 glass-card glass-card-strong p-4 pr-3',
        variantStyles[toast.variant]
      )}
    >
      <span
        aria-hidden
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[color:var(--accent)] font-bold ring-1 ring-[color:var(--accent)]/40 bg-[color:var(--accent)]/10"
      >
        {variantIcon[toast.variant]}
      </span>
      <div className="flex flex-1 flex-col gap-0.5">
        <p className="font-medium text-white">{toast.title}</p>
        {toast.description && (
          <p className="text-sm text-[color:var(--color-text-secondary)]">{toast.description}</p>
        )}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => remove(id)}
        className="-m-1 rounded-md p-1 text-[color:var(--color-text-secondary)] transition-colors hover:bg-white/10 hover:text-white"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </motion.div>
  );
}

export function ToastViewport() {
  const toasts = useUIStore((s) => s.toasts);
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-6 right-6 z-[1100] flex flex-col gap-3 w-fit"
    >
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>
  );
}
